import React, { useState, useMemo } from "react";
import {
  Box,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Typography,
  IconButton,
  makeStyles,
  FormControl,
  TextField,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  Select,
  MenuItem,
} from "@material-ui/core";
import axios from "axios";
import { URL } from "../../config/config";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import LockIcon from "@material-ui/icons/Lock";
import LockOpenIcon from "@material-ui/icons/LockOpen";
import VpnKeyIcon from "@material-ui/icons/VpnKey";
import SecurityIcon from "@material-ui/icons/Security";
import SecurityOutlinedIcon from "@material-ui/icons/SecurityOutlined";
import BlockIcon from "@material-ui/icons/Block";
import NoEncryptionIcon from "@material-ui/icons/NoEncryption";
import WarningIcon from "@material-ui/icons/Warning";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import ErrorIcon from "@material-ui/icons/Error";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import DoneIcon from "@material-ui/icons/Done";

const useStyles = makeStyles({
  title: {
    padding: "16px 24px",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    gap: "1.5rem",
  },
  dialogContent: {
    overflowY: "auto",
    maxHeight: "calc(100vh - 200px)",
  },
  iconGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))",
    gap: "1rem",
    marginTop: "1rem",
    "@media (min-width: 600px)": {
      gridTemplateColumns: "repeat(5, 1fr)",
    },
  },
  iconOption: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "10px",
    border: "2px solid #e0e0e0",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "all 0.2s",
    "&:hover": {
      borderColor: "#0a474c",
      backgroundColor: "#f5f5f5",
    },
  },
  iconOptionSelected: {
    borderColor: "#0a474c",
    backgroundColor: "#e8f4f5",
  },
  iconWrapper: {
    fontSize: "1.5rem",
    color: "#0a474c",
    marginBottom: "0.5rem",
  },
  iconLabel: {
    fontSize: "0.75rem",
    textAlign: "center",
    color: "#666",
  },
});

const LOCK_ICONS = [
  { name: "CheckCircle", component: CheckCircleIcon, label: "Check Circle", category: "success" },
  { name: "CheckCircleOutline", component: CheckCircleOutlineIcon, label: "Check Circle Outline", category: "success" },
  { name: "Done", component: DoneIcon, label: "Done", category: "success" },
  { name: "Warning", component: WarningIcon, label: "Warning", category: "warning" },
  { name: "ErrorOutline", component: ErrorOutlineIcon, label: "Error Outline", category: "warning" },
  { name: "Error", component: ErrorIcon, label: "Error", category: "warning" },
  { name: "Lock", component: LockIcon, label: "Lock", category: "disabled" },
  { name: "Security", component: SecurityIcon, label: "Security", category: "disabled" },
  { name: "Block", component: BlockIcon, label: "Block", category: "disabled" },
  { name: "NoEncryption", component: NoEncryptionIcon, label: "No Encryption", category: "disabled" },
];

const ICON_CATEGORIES = [
  { key: "success", label: "Success" },
  { key: "warning", label: "Warning" },
  { key: "disabled", label: "Disabled" },
];

function DashboardLockModal({ onClose, onSuccess, onError, assistantId }) {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [config, setConfig] = useState({
    title: "",
    description: "",
    theme: "",
    icon: "",
    iconColor: "#0a474c",
    buttons: [],
    warningCount: 1,
  });

  const [touchedFields, setTouchedFields] = useState({
    title: false,
    description: false,
  });
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const validationErrors = useMemo(() => {
    const errs = {};

    // Title: min 5, max 50 (using trimmed length)
    const rawTitle = config.title || "";
    const title = rawTitle.trim();
    const titleLength = title.length;
    if (titleLength < 5) {
      errs.title = "Minimum 5 characters required";
    } else if (titleLength > 50) {
      errs.title = "Maximum 50 characters allowed";
    }

    // Description: min 10, max 1000 (using trimmed length)
    const rawDescription = config.description || "";
    const description = rawDescription.trim();
    const descriptionLength = description.length;
    if (descriptionLength < 10) {
      errs.description = "Minimum 10 characters required";
    } else if (descriptionLength > 1000) {
      errs.description = "Maximum 1000 characters allowed";
    }

    if (!config.theme) errs.theme = "Theme is required";

    if (config.theme === "yellow") {
      const count = Number(config.warningCount);
      if (!Number.isInteger(count) || count < 1) {
        errs.warningCount = "Enter how many times to show the warning (min 1, max 10)";
      } else if (count > 10) {
        errs.warningCount = "Maximum count is 10";
      }
    }

    if (Array.isArray(config.buttons) && config.buttons.length) {
      const buttonLabelsErrors = [];
      config.buttons.forEach((button, index) => {
        const label = (button?.label || "").trim();
        if (label.length > 50) {
          buttonLabelsErrors[index] = "Maximum 50 characters allowed";
        }
      });
      if (buttonLabelsErrors.length) {
        errs.buttonLabels = buttonLabelsErrors;
      }
    }

    if (!config.icon) errs.icon = "Icon selection is required";

    return errs;
  }, [config.title, config.description, config.theme, config.warningCount, config.icon, config.buttons]);

  const maxButtons = config.theme === "red" ? 1 : 2;

  const updateConfig = (key, value) => {
    setConfig((prevState) => {
      const next = { ...prevState, [key]: value };
      if (key === "theme") {
        if (value === "red") {
          const contactSupport = next.buttons?.find((btn) => btn.action === "CONTACT_SUPPORT");
          next.buttons = contactSupport
            ? [{ ...contactSupport, position: "left" }]
            : [
                {
                  label: "",
                  action: "CONTACT_SUPPORT",
                  color: "#0a474c",
                  style: "background",
                  position: "left",
                },
              ];
        } else if (
          (value === "green" || value === "yellow") &&
          (!next.buttons || next.buttons.length === 0)
        ) {
          next.buttons = [
            {
              label: "",
              action: "ACKNOWLEDGE",
              color: "#0a474c",
              style: "background",
              position: "left",
            },
          ];
        }
      }
      return next;
    });
  };

  const handleButtonChange = (index, key, value) => {
    const buttons = [...config.buttons];
    buttons[index] = {
      ...buttons[index],
      [key]: value,
    };
    updateConfig("buttons", buttons);
  };

  const addButton = () => {
    if (config.buttons.length >= maxButtons) {
      setError(`Maximum ${maxButtons} button${maxButtons === 1 ? "" : "s"} allowed`);
      return;
    }
    const buttons = [...config.buttons];
    const usedActions = buttons
      .map((b) => b.action)
      .filter(Boolean);
    const usedPositions = buttons
      .map((b) => b.position)
      .filter(Boolean);

    const options =
      config.theme === "red"
        ? ["CONTACT_SUPPORT"]
        : config.theme === "green" || config.theme === "yellow"
        ? ["ACKNOWLEDGE", "CONTACT_SUPPORT"]
        : ["CONTACT_SUPPORT", "ACKNOWLEDGE"];
    const firstUnused =
      options.find((action) => !usedActions.includes(action)) || options[0];
    const defaultAction = firstUnused;

    let defaultPosition = "left";
    if (usedPositions.includes("left") && !usedPositions.includes("right")) {
      defaultPosition = "right";
    } else if (
      usedPositions.includes("right") &&
      !usedPositions.includes("left")
    ) {
      defaultPosition = "left";
    }

    buttons.push({
      label: "",
      action: defaultAction,
      color: "#0a474c",
      style: "background",
      position: defaultPosition,
    });
    updateConfig("buttons", buttons);
  };

  const removeButton = (index) => {
    const button = config.buttons[index];
    if (
      (config.theme === "green" || config.theme === "yellow") &&
      button?.action === "ACKNOWLEDGE"
    ) {
      return;
    }
    if (config.theme === "red" && button?.action === "CONTACT_SUPPORT") {
      return;
    }
    const buttons = [...config.buttons];
    buttons.splice(index, 1);
    updateConfig("buttons", buttons);
  };

  const handleIconSelect = (iconName) => {
    updateConfig("icon", iconName);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);
    if (Object.keys(validationErrors).length > 0) {
      setError("Please complete all required fields.");
      return;
    }
    setError("");
    setLoading(true);

    try {
      const requestBody = {
        assistantId: assistantId,
        title: config.title,
        description: config.description,
        theme: config.theme,
        icon: config.icon,
        iconColor: config.iconColor,
        buttonObject: config.buttons.map(button => {
          const label =
            button.label && button.label.trim()
              ? button.label.trim()
              : button.action === "ACKNOWLEDGE"
              ? "Acknowledged"
              : "Contact Support";
          return {
            btnLabel: label,
            btnAction: button.action,
            btnType: button.style || "background",
            btnColor: button.color || "#0a474c",
            btnPosition: button.position || null,
          };
        }),
        lockMode:
          config.theme === "red"
            ? "lockNow"
            : config.theme === "yellow"
            ? "warnUser"
            : "notifyUser",
        ...(config.theme === "yellow" && {
          lockCount: Math.min(10, Math.max(1, Number(config.warningCount) || 1)),
        }),
      };

      const response = await axios.post(
        `${URL}/superadmin/dashboard-lock`,
        requestBody,
        {
          headers: {
            "Content-Type": "application/json",
            "x-request-source": "superadmin",
          },
        }
      );

      if (onSuccess) {
        onSuccess(response.data);
      }
      onClose();
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Something went wrong";
      setError(message);
      if (onError) {
        onError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <DialogTitle className={classes.title}>
        <Grid container alignItems="center" spacing={2}>
          <Grid item>
            <Typography variant="h3" component="p">
              Configure Dashboard
            </Typography>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent dividers className={classes.dialogContent}>
        <form id="dashboard-lock-form" onSubmit={handleSubmit} className={classes.form}>
          {error && (
            <Box
              style={{
                padding: "0.75rem",
                backgroundColor: "#ffebee",
                color: "#c62828",
                borderRadius: "4px",
                marginBottom: "1rem",
              }}
            >
              <Typography variant="body2">{error}</Typography>
            </Box>
          )}
          <FormControl fullWidth>
            <Typography variant="body1" gutterBottom>
              Title *
            </Typography>
            <TextField
              value={config.title}
              onChange={(e) => updateConfig("title", e.target.value)}
              onBlur={() =>
                setTouchedFields((prev) => ({ ...prev, title: true }))
              }
              placeholder="Dashboard locked"
              error={
                !!validationErrors.title &&
                (touchedFields.title || submitAttempted)
              }
            />
            {(touchedFields.title || submitAttempted) &&
              validationErrors.title && (
                <Typography
                  variant="caption"
                  style={{ color: "#f44336", marginTop: 4 }}
                >
                  {validationErrors.title}
                </Typography>
              )}
          </FormControl>

          <FormControl fullWidth>
            <Typography variant="body1" gutterBottom>
              Description *
            </Typography>
            <TextField
              multiline
              rows={4}
              value={config.description}
              onChange={(e) => updateConfig("description", e.target.value)}
              onBlur={() =>
                setTouchedFields((prev) => ({ ...prev, description: true }))
              }
              placeholder="Description for Locking the Dashboard"
              error={
                !!validationErrors.description &&
                (touchedFields.description || submitAttempted)
              }
            />
            {(touchedFields.description || submitAttempted) &&
              validationErrors.description && (
                <Typography
                  variant="caption"
                  style={{ color: "#f44336", marginTop: 4 }}
                >
                  {validationErrors.description}
                </Typography>
              )}
          </FormControl>

          <FormControl fullWidth>
            <Typography variant="body1" gutterBottom>
              Theme *
            </Typography>
            <RadioGroup
              value={config.theme}
              onChange={(e) => updateConfig("theme", e.target.value)}
              row
              style={{ width: "100%" }}
            >
              <FormControlLabel
                value="green"
                control={<Radio color="primary" />}
                label="Success"
              />
              <FormControlLabel
                value="yellow"
                control={<Radio color="primary" />}
                label="Warning"
              />
              <FormControlLabel
                value="red"
                control={<Radio color="primary" />}
                label="Disabled"
              />
            </RadioGroup>
            {config.theme && (
              <Box
                style={{
                  marginTop: 12,
                  padding: "12px 16px",
                  borderRadius: 8,
                  border: "1px solid #e0e0e0",
                  backgroundColor:
                    config.theme === "red"
                      ? "#ffebee"
                      : config.theme === "yellow"
                      ? "#fff8e1"
                      : "#e8f5e9",
                }}
              >
                <Typography
                  variant="body2"
                  style={{
                    fontWeight: 600,
                    color: "#37474f",
                    marginBottom: 4,
                  }}
                >
                  Lock mode
                </Typography>
                <Typography variant="body1" style={{ color: "#263238" }}>
                  {config.theme === "red"
                    ? "Lock User"
                    : config.theme === "yellow"
                    ? "Warn User"
                    : "Notify User"}
                </Typography>
              </Box>
            )}
          </FormControl>

          {config.theme === "yellow" && (
            <FormControl fullWidth error={!!validationErrors.warningCount}>
              <Typography variant="body1" gutterBottom>
                How many times to show warning *
              </Typography>
              <TextField
                type="number"
                value={config.warningCount}
                onChange={(e) => updateConfig("warningCount", e.target.value)}
                placeholder="1"
                inputProps={{ min: 1, max: 10, step: 1 }}
                error={!!validationErrors.warningCount}
                helperText={validationErrors.warningCount}
              />
            </FormControl>
          )}

          <FormControl fullWidth>
            <Typography variant="body1" gutterBottom>
              Icon *
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: "0.5rem" }}>
              Select an icon for the dashboard lock modal
            </Typography>
            {ICON_CATEGORIES.map((cat) => {
              const iconsInCategory = LOCK_ICONS.filter((icon) => icon.category === cat.key);
              if (iconsInCategory.length === 0) return null;
              return (
                <Box key={cat.key} style={{ marginBottom: "1.5rem" }}>
                  <Typography
                    variant="body2"
                    style={{
                      fontWeight: 600,
                      color: "#37474f",
                      marginBottom: "0.5rem",
                    }}
                  >
                    {cat.label}
                  </Typography>
                  <Box className={classes.iconGrid}>
                    {iconsInCategory.map((icon) => {
                      const IconComponent = icon.component;
                      const isSelected = config.icon === icon.name;
                      return (
                        <Box
                          key={icon.name}
                          className={`${classes.iconOption} ${isSelected ? classes.iconOptionSelected : ""}`}
                          onClick={() => handleIconSelect(icon.name)}
                        >
                          <IconComponent
                            className={classes.iconWrapper}
                            style={{
                              color: isSelected ? config.iconColor : "#0a474c",
                            }}
                          />
                          <Typography variant="caption" className={classes.iconLabel}>
                            {icon.label}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              );
            })}
            {config.icon && (
              <Box style={{ marginTop: "1rem" }}>
                <Typography variant="body2" gutterBottom>
                  Icon Color
                </Typography>
                <Box
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "1rem",
                  }}
                >
                  <input
                    type="color"
                    value={config.iconColor}
                    onChange={(e) => updateConfig("iconColor", e.target.value)}
                    style={{
                      width: "60px",
                      height: "40px",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                      cursor: "pointer",
                    }}
                  />
                  <TextField
                    value={config.iconColor}
                    onChange={(e) => updateConfig("iconColor", e.target.value)}
                    placeholder="#0a474c"
                    size="small"
                    style={{ width: "120px" }}
                  />
                  <Box
                    style={{
                      width: "40px",
                      height: "40px",
                      backgroundColor: config.iconColor,
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                    }}
                  />
                </Box>
              </Box>
            )}
          </FormControl>

          <FormControl fullWidth>
            <Typography variant="body1" gutterBottom>
              Buttons
            </Typography>
            <Box
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
              }}
            >
              {config.buttons.map((button, index) => {
                const otherButtons = config.buttons.filter((_, i) => i !== index);
                const otherPositions = otherButtons
                  .map((b) => b.position)
                  .filter(Boolean);
                const leftTaken = otherPositions.includes("left");
                const rightTaken = otherPositions.includes("right");

                const otherActions = otherButtons
                  .map((b) => b.action)
                  .filter(Boolean);
                const contactSupportTaken = otherActions.includes(
                  "CONTACT_SUPPORT"
                );
                const acknowledgeTaken = otherActions.includes("ACKNOWLEDGE");

                return (
                  <Box
                    key={index}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.5rem",
                      padding: "1rem",
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",
                    }}
                  >
                    <Box
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                      }}
                    >
                      <Select
                        value={button.action || "CONTACT_SUPPORT"}
                        onChange={(e) =>
                          handleButtonChange(index, "action", e.target.value)
                        }
                        displayEmpty
                        style={{ minWidth: "200px" }}
                        disabled={
                          (config.theme === "green" ||
                            config.theme === "yellow") &&
                          button.action === "ACKNOWLEDGE"
                        }
                      >
                        <MenuItem
                          value="CONTACT_SUPPORT"
                          disabled={contactSupportTaken}
                        >
                          Contact Support
                        </MenuItem>
                        <MenuItem
                          value="ACKNOWLEDGE"
                          disabled={acknowledgeTaken || config.theme === "red"}
                        >
                          Acknowledged
                        </MenuItem>
                      </Select>
                      <TextField
                        value={button.label}
                        onChange={(e) =>
                          handleButtonChange(index, "label", e.target.value)
                        }
                        placeholder="Button Label"
                        error={!!validationErrors.buttonLabels?.[index]}
                        helperText={validationErrors.buttonLabels?.[index]}
                        style={{ flex: 1 }}
                      />
                      {!(
                        ((config.theme === "green" ||
                          config.theme === "yellow") &&
                          button.action === "ACKNOWLEDGE") ||
                        (config.theme === "red" &&
                          button.action === "CONTACT_SUPPORT")
                      ) && (
                        <IconButton
                          onClick={() => removeButton(index)}
                          style={{ color: "#0a474c" }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Box>
                    <Box
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "1rem",
                      }}
                    >
                      <Typography variant="body2" style={{ minWidth: "80px" }}>
                        Button Color:
                      </Typography>
                      <input
                        type="color"
                        value={button.color || "#0a474c"}
                        onChange={(e) =>
                          handleButtonChange(index, "color", e.target.value)
                        }
                        style={{
                          width: "60px",
                          height: "40px",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                          cursor: "pointer",
                        }}
                      />
                      <TextField
                        value={button.color || "#0a474c"}
                        onChange={(e) =>
                          handleButtonChange(index, "color", e.target.value)
                        }
                        placeholder="#0a474c"
                        size="small"
                        style={{ width: "120px" }}
                      />
                      <Box
                        style={{
                          width: "40px",
                          height: "40px",
                          backgroundColor: button.color || "#0a474c",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                        }}
                      />
                    </Box>
                    <Box
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "1rem",
                        marginTop: "0.5rem",
                      }}
                    >
                      <Typography variant="body2" style={{ minWidth: "100px" }}>
                        Button Style:
                      </Typography>
                      <Select
                        value={button.style || "background"}
                        onChange={(e) =>
                          handleButtonChange(index, "style", e.target.value)
                        }
                        displayEmpty
                        style={{ minWidth: "150px" }}
                      >
                        <MenuItem value="background">Background Color</MenuItem>
                        <MenuItem value="border">Border Color</MenuItem>
                      </Select>
                    </Box>
                    <Box
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "1rem",
                        marginTop: "0.5rem",
                      }}
                    >
                      <Typography variant="body2" style={{ minWidth: "100px" }}>
                        Button Position:
                      </Typography>
                      <Select
                        value={button.position || (index === 0 ? "left" : "right")}
                        onChange={(e) =>
                          handleButtonChange(index, "position", e.target.value)
                        }
                        displayEmpty
                        style={{ minWidth: "150px" }}
                      >
                        <MenuItem value="left" disabled={leftTaken}>
                          Left
                        </MenuItem>
                        <MenuItem value="right" disabled={rightTaken}>
                          Right
                        </MenuItem>
                      </Select>
                    </Box>
                  </Box>
                );
              })}
              <Button
                variant={config.buttons.length >= maxButtons ? "contained" : "outlined"}
                color="primary"
                startIcon={<AddIcon />}
                onClick={addButton}
                disabled={config.buttons.length >= maxButtons}
                style={{
                  marginTop: "0.5rem",
                  ...(config.buttons.length >= maxButtons
                    ? {
                        backgroundColor: "#e0e0e0",
                        color: "#9e9e9e",
                        border: "none",
                        cursor: "not-allowed",
                        opacity: 1,
                      }
                    : {
                        borderColor: "#0a474c",
                        color: "#0a474c",
                        cursor: "pointer",
                      }),
                }}
              >
                Add Button{" "}
                {config.buttons.length >= maxButtons
                  ? `(Max ${maxButtons})`
                  : `(${config.buttons.length}/${maxButtons})`}
              </Button>
            </Box>
          </FormControl>

        </form>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          color="default"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          form="dashboard-lock-form"
          variant="contained"
          color="primary"
          disabled={loading || Object.keys(validationErrors).length > 0}
          style={{
            backgroundColor:
              loading || Object.keys(validationErrors).length > 0
                ? "#e0e0e0"
                : "#0a474c",
            color:
              loading || Object.keys(validationErrors).length > 0
                ? "#9e9e9e"
                : undefined,
            opacity: loading || Object.keys(validationErrors).length > 0 ? 1 : undefined,
          }}
        >
          {!loading ? "Save Configuration" : "Saving..."}
        </Button>
      </DialogActions>
    </>
  );
}

export default DashboardLockModal;
